import React, { useMemo, Suspense, useRef } from 'react'
import { useTexture } from '@react-three/drei'
import { useDesignStore } from '../../store/designStore'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'

const PanoramicBackgroundInner: React.FC = () => {
  const { canvasSettings } = useDesignStore()
  const { camera } = useThree()
  const sphereRef = useRef<THREE.Mesh>(null)
  
  if (!canvasSettings.backgroundImage) {
    return null
  }

  const texture = useTexture(canvasSettings.backgroundImage, (texture) => {
    texture.wrapS = THREE.RepeatWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter
    texture.flipY = false
  })

  // Calculate sphere dimensions and transformations
  const sphereRadius = useMemo(() => {
    const baseRadius = Math.max(canvasSettings.width, canvasSettings.height) * 2
    return baseRadius * (canvasSettings.backgroundImageScale || 1.0)
  }, [canvasSettings.width, canvasSettings.height, canvasSettings.backgroundImageScale])

  const spherePosition = useMemo(() => {
    return [
      canvasSettings.backgroundImageOffsetX || 0,
      canvasSettings.backgroundImageOffsetY || 0,
      canvasSettings.backgroundImageOffsetZ || 0
    ] as [number, number, number]
  }, [canvasSettings.backgroundImageOffsetX, canvasSettings.backgroundImageOffsetY, canvasSettings.backgroundImageOffsetZ])

  const sphereRotation = useMemo(() => {
    return [
      canvasSettings.backgroundImageRotationX || 0,
      canvasSettings.backgroundImageRotationY || 0,
      canvasSettings.backgroundImageRotationZ || 0
    ] as [number, number, number]
  }, [canvasSettings.backgroundImageRotationX, canvasSettings.backgroundImageRotationY, canvasSettings.backgroundImageRotationZ])

  // Update sphere position to follow camera
  useFrame(() => {
    if (sphereRef.current && camera) {
      // Make the panorama follow the camera position but maintain relative offset
      const cameraPosition = camera.position
      sphereRef.current.position.set(
        cameraPosition.x + (canvasSettings.backgroundImageOffsetX || 0),
        cameraPosition.y + (canvasSettings.backgroundImageOffsetY || 0),
        cameraPosition.z + (canvasSettings.backgroundImageOffsetZ || 0)
      )
    }
  })

  return (
    <mesh
      ref={sphereRef}
      rotation={sphereRotation}
    >
      {/* Use sphere geometry for 360-degree panorama */}
      <sphereGeometry args={[sphereRadius, 64, 32]} />
      <meshBasicMaterial
        map={texture}
        transparent
        opacity={canvasSettings.backgroundImageOpacity || 0.7}
        side={THREE.BackSide} // Render on the inside of the sphere
        depthWrite={false}
        depthTest={false}
      />
    </mesh>
  )
}

const PanoramicBackground: React.FC = () => {
  const { canvasSettings } = useDesignStore()
  
  if (!canvasSettings.backgroundImage) {
    return null
  }

  return (
    <Suspense fallback={null}>
      <PanoramicBackgroundInner />
    </Suspense>
  )
}

export default PanoramicBackground
